import { Box, Typography } from '@mui/material';
import { combineSX } from '@/utils/combineSX';
import {
	getRepurchaseSavings,
	useActivatedFromCart,
	useRepurchaseCouponActions,
	useStoredRepurchaseCoupon,
	StoredRepurchaseCoupon,
} from '@/data/Content/RepurchaseCouponState';
import { OrderAdjustment } from '@/data/types/Order';
import { getCouponValidityHint } from '@/components/blocks/DpRepurchaseCoupon/formatCouponDate';
import {
	CouponCheckIcon,
	CouponTicketIcon,
} from '@/components/blocks/DpRepurchaseCoupon/parts/CouponIcons';
import { RepurchaseActivateCheck } from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseActivateCheck';
import {
	flowCardRowSX,
	flowCartCardSX,
	flowInlineCardSX,
	flowMiniCartCardSX,
	flowPdpCardSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/flow';
import {
	repurchaseCouponHintSX,
	repurchaseCouponTextSX,
	repurchaseStrongSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/typography';

type CouponProps = { coupon: StoredRepurchaseCoupon };

const isAppliedToOrder = (coupon: StoredRepurchaseCoupon, adjustment: OrderAdjustment[]) =>
	coupon.status === 'APPLIED' && getRepurchaseSavings(adjustment, coupon.promotionName) > 0;

const Strong = ({ children }: { children: string }) => (
	<Typography component="strong" sx={repurchaseStrongSX}>
		{children}
	</Typography>
);

const AppliedLine = ({ percentage }: { percentage: number }) => (
	<Box sx={flowCardRowSX}>
		<CouponCheckIcon />
		<CouponTicketIcon width={24} height={22} />
		<Typography component="span" sx={repurchaseCouponTextSX}>
			<Strong>{`${percentage}% OFF`}</Strong>
			{' aplicado'}
		</Typography>
	</Box>
);

const RepurchaseAppliedCard = ({ coupon, miniCart }: CouponProps & { miniCart?: boolean }) => (
	<Box
		role="status"
		sx={combineSX([miniCart ? flowMiniCartCardSX : flowCartCardSX, flowInlineCardSX])}
	>
		<AppliedLine percentage={coupon.percentage} />
	</Box>
);

export const RepurchaseAppliedNotice = ({
	withCartBox = false,
	adjustment = [],
}: {
	withCartBox?: boolean;
	adjustment?: OrderAdjustment[];
}) => {
	const coupon = useStoredRepurchaseCoupon();
	const activatedFromCart = useActivatedFromCart();
	return coupon && isAppliedToOrder(coupon, adjustment) && !(withCartBox && activatedFromCart) ? (
		<RepurchaseAppliedCard coupon={coupon} />
	) : null;
};

export const RepurchasePdpCard = ({ coupon, miniCart }: CouponProps & { miniCart?: boolean }) => {
	const { applyCoupon } = useRepurchaseCouponActions();
	const hint = getCouponValidityHint(coupon.expirationDateTime);
	const showHint = !miniCart && hint !== '';

	if (coupon.status === 'APPLIED') {
		return (
			<Box
				role="status"
				sx={combineSX([miniCart ? flowMiniCartCardSX : flowPdpCardSX, flowInlineCardSX])}
			>
				<AppliedLine percentage={coupon.percentage} />
			</Box>
		);
	}

	return (
		<RepurchaseActivateCheck
			percentage={coupon.percentage}
			expirationDateTime={coupon.expirationDateTime}
			onApply={applyCoupon}
			source={miniCart ? 'quick_cart' : 'pdp'}
			origin={miniCart ? 'quick_cart' : 'unknown'}
			sx={miniCart ? combineSX([flowMiniCartCardSX, flowInlineCardSX]) : flowPdpCardSX}
			urgencyBelow={!miniCart}
		>
			{showHint ? (
				<Typography component="span" sx={repurchaseCouponHintSX}>
					{hint}
				</Typography>
			) : null}
		</RepurchaseActivateCheck>
	);
};

export const RepurchaseMiniCartCard = ({ adjustment = [] }: { adjustment?: OrderAdjustment[] }) => {
	const coupon = useStoredRepurchaseCoupon();
	if (!coupon || (coupon.status === 'APPLIED' && !isAppliedToOrder(coupon, adjustment))) {
		return null;
	}
	return <RepurchasePdpCard coupon={coupon} miniCart />;
};
